import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { OrganizationType } from '@/shared/types/organization.type'

const SESSION_STORAGE_KEY = 'turing_selected_organization'

export const useOrganizationStore = defineStore('organization', () => {
  // State
  const currentOrganization = ref<OrganizationType | null>(null)
  const organizations = ref<OrganizationType[]>([])
  const organizationsLoaded = ref(false)

  // Getters
  const hasOrganization = computed(() => currentOrganization.value !== null)

  const currentOrganizationId = computed(() => currentOrganization.value?.uuid)

  const hasSingleOrganization = computed(() => organizations.value.length === 1)

  // Actions

  /**
   * Set the current organization and persist to sessionStorage
   */
  const setCurrentOrganization = (org: OrganizationType): void => {
    currentOrganization.value = org
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(org))
    } catch (error) {
      console.warn('Failed to persist organization to sessionStorage:', error)
    }
  }

  /**
   * Clear current organization from state and sessionStorage
   */
  const clearOrganization = (): void => {
    currentOrganization.value = null
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear organization from sessionStorage:', error)
    }
  }

  /**
   * Initialize organization from sessionStorage on app start
   * Returns true if an organization was restored from session
   */
  const initializeFromSession = (): boolean => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
      if (stored) {
        const org = JSON.parse(stored) as OrganizationType
        // Validate the stored organization has required fields
        if (org && typeof org.uuid === 'string' && typeof org.title === 'string') {
          currentOrganization.value = org
          return true
        }
      }
    } catch (error) {
      console.warn('Failed to restore organization from sessionStorage:', error)
    }
    return false
  }

  /**
   * Set the list of available organizations
   */
  const setOrganizations = (orgs: OrganizationType[]): void => {
    organizations.value = orgs
    organizationsLoaded.value = true
  }

  /**
   * Clear all organization state (used during logout or refresh)
   */
  const clearAllOrganizationState = (): void => {
    currentOrganization.value = null
    organizations.value = []
    organizationsLoaded.value = false
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear organization from sessionStorage:', error)
    }
  }

  return {
    // State
    currentOrganization,
    organizations,
    organizationsLoaded,
    // Getters
    hasOrganization,
    currentOrganizationId,
    hasSingleOrganization,
    // Actions
    setCurrentOrganization,
    clearOrganization,
    initializeFromSession,
    setOrganizations,
    clearAllOrganizationState
  }
})
